import { useEffect, RefObject } from "react"
import { SVGElementSelection, Table } from "../types"

interface UseSvgOverlaysProps {
  containerRef: RefObject<HTMLDivElement>
  selectedElements: SVGElementSelection[]
  showAssignedTables: boolean
  svgContent: string | null
  tables: Table[]
  selectedTableId: string | null
  findElementById: (id: string) => SVGElement | null
}

export const useSvgOverlays = ({
  containerRef,
  selectedElements,
  showAssignedTables,
  svgContent,
  tables,
  selectedTableId,
  findElementById,
}: UseSvgOverlaysProps) => {
  useEffect(() => {
    if (!containerRef.current) return

    const svgElement = containerRef.current.querySelector("svg")
    if (!svgElement) return

    const existingHighlights = svgElement.querySelectorAll("[data-tabb-highlight-overlay]")
    existingHighlights.forEach((highlight) => highlight.remove())

    selectedElements.forEach((selection) => {
      const currentElement = selection.element.id ? findElementById(selection.element.id) : selection.element

      if (currentElement) {
        try {
          const graphicsElement = currentElement as SVGGraphicsElement

          const highlightClone = graphicsElement.cloneNode(true) as SVGGraphicsElement
          highlightClone.setAttribute("data-tabb-highlight-overlay", "true")
          highlightClone.style.pointerEvents = "none"
          highlightClone.style.fill = "#1976d2"
          highlightClone.style.stroke = "#1976d2"
          highlightClone.style.strokeWidth = "2"
          highlightClone.style.opacity = "0.2"
          highlightClone.removeAttribute("data-tabb-table")
          highlightClone.removeAttribute("id")

          const allChildren = highlightClone.querySelectorAll("*")
          allChildren.forEach((child) => {
            ;(child as SVGElement).style.fill = "#1976d2"
            ;(child as SVGElement).style.stroke = "#1976d2"
          })

          const parent = graphicsElement.parentElement
          if (parent) {
            parent.insertBefore(highlightClone, graphicsElement.nextSibling)
          }
        } catch (e) {
          console.warn("Failed to create highlight overlay for element", e)
        }
      }
    })
  }, [selectedElements, containerRef, findElementById])

  useEffect(() => {
    if (!containerRef.current || !showAssignedTables) {
      const container = containerRef.current
      if (container) {
        const svgElement = container.querySelector("svg")
        if (svgElement) {
          const existingOverlays = svgElement.querySelectorAll("[data-tabb-overlay]")
          existingOverlays.forEach((overlay) => overlay.remove())
        }
      }
      return
    }

    const container = containerRef.current
    const svgElement = container.querySelector("svg")
    if (!svgElement) return

    const existingOverlays = svgElement.querySelectorAll("[data-tabb-overlay]")
    existingOverlays.forEach((overlay) => overlay.remove())

    const viewBox = svgElement.viewBox.baseVal
    const svgWidth = viewBox.width || svgElement.getBoundingClientRect().width
    const svgHeight = viewBox.height || svgElement.getBoundingClientRect().height

    const baseFontSize = Math.min(svgWidth, svgHeight) * 0.03

    const elementsWithTables = svgElement.querySelectorAll("[data-tabb-table]")
    elementsWithTables.forEach((element) => {
      const tableId = element.getAttribute("data-tabb-table")
      if (!tableId) return

      const table = tables.find((t) => t.id === tableId)
      if (!table) return

      try {
        const graphicsElement = element as SVGGraphicsElement
        const bbox = graphicsElement.getBBox()

        const overlayGroup = document.createElementNS("http://www.w3.org/2000/svg", "g")
        overlayGroup.setAttribute("data-tabb-overlay", "true")
        overlayGroup.style.pointerEvents = "none"

        const clonedElement = graphicsElement.cloneNode(true) as SVGGraphicsElement
        clonedElement.style.fill = "rgba(14, 165, 233, 0.2)"
        clonedElement.style.stroke = "none"
        clonedElement.style.opacity = "1"
        clonedElement.removeAttribute("data-tabb-table")
        clonedElement.removeAttribute("id")

        const allChildren = clonedElement.querySelectorAll("*")
        allChildren.forEach((child) => {
          ;(child as SVGElement).style.fill = "rgba(14, 165, 233, 0.2)"
          ;(child as SVGElement).style.stroke = "none"
        })

        const overlayText = document.createElementNS("http://www.w3.org/2000/svg", "text")
        overlayText.setAttribute("x", (bbox.x + bbox.width / 2).toString())
        overlayText.setAttribute("y", (bbox.y + bbox.height / 2).toString())
        overlayText.setAttribute("text-anchor", "middle")
        overlayText.setAttribute("dominant-baseline", "middle")
        overlayText.setAttribute("fill", "#0369a1")
        overlayText.setAttribute("font-size", baseFontSize.toString())
        overlayText.setAttribute("font-weight", "600")
        overlayText.textContent = table.name

        const parent = graphicsElement.parentElement
        if (parent) {
          overlayGroup.appendChild(clonedElement)
          overlayGroup.appendChild(overlayText)
          parent.insertBefore(overlayGroup, graphicsElement.nextSibling)
        }
      } catch (e) {
        console.warn("Failed to create overlay for element", e)
      }
    })
  }, [svgContent, tables, showAssignedTables, selectedTableId, containerRef])
}
